import React, { ReactNode, useCallback, useState, useEffect, useRef } from 'react';
import cn from '@helpers/className';
import { ChevronUpIcon } from '@resortpass/solar-icons';
import Paragraph from '@solar/common/Paragraph/Paragraph';
import useRect from '@hooks/useRect';
import styles from './CollapsibleContainer.module.css';

type Props = {
  title: string;
  children: ReactNode;
  height: string;
  noBorder?: boolean;
};

export default function CollapsibleContainer({ title, height, children, noBorder = false }: Props) {
  const [isExpanded, setIsExpanded] = useState<boolean>(true);

  const toggleExpanded = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  return (
    <div className="w-full mt-5 overflow-hidden">
      <div
        className="flex justify-between items-center pb-5 cursor-pointer"
        onClick={toggleExpanded}
        onKeyDown={toggleExpanded}
        role="button"
        tabIndex={0}
      >
        <p className="font-rp-pn-semi-bold text-19">{title}</p>

        <div className={`h-7 w-7 relative ${!isExpanded ? 'transform rotate-180 mr-3' : 'mr-2'}`}>
          <ChevronUpIcon />
        </div>
      </div>
      <div
        className={`w-full transform transition-all ease-in-out duration-300 text-base border-rp-gray-divider overflow-hidden ${
          styles.collapsibleContainer
        } ${noBorder ? 'border-b-0' : 'border-b'}`}
        style={
          isExpanded
            ? { height: `${height}`, overflow: 'hidden' }
            : { height: '0px', overflowY: 'hidden' }
        }
      >
        {children}
      </div>
    </div>
  );
}

type Props2 = {
  title: string | ReactNode;
  noBorder?: boolean;
  children: ReactNode;
  className?: string;
};

export function CollapsibleContainer2({ title, children, className, noBorder = false }: Props2) {
  const isNoBorder = !!noBorder;

  // inbetween renders of new content/state, keep the expanded state
  const expandedRef = useRef(true);
  const [isExpanded, setIsExpanded] = useState<boolean>(expandedRef.current);
  const toggleExpanded = () => setIsExpanded(!isExpanded);
  const parentContainerRef = useRef<HTMLDivElement>(null);
  const [childContainerRef, childContainerUpdate] = useRect<HTMLDivElement>(
    ['resize'],
    { passive: true, capture: true },
    0,
  );
  const rect = useRef<DOMRect | null>(null);

  // auto update the expandedRefState on every state change
  useEffect(() => {
    expandedRef.current = isExpanded;
  }, [isExpanded]);

  // This gets the height of the container in its expanded state.
  // This is needed to animate is expanded or collapsed.
  useEffect(() => {
    if (!childContainerRef.current || !parentContainerRef.current) return;
    const oldHeight = parentContainerRef.current.style.height;
    parentContainerRef.current.style.height = 'auto';
    rect.current = parentContainerRef.current.getBoundingClientRect();
    parentContainerRef.current.style.height = oldHeight;
  }, [childContainerRef, childContainerUpdate]);

  // Update the container height based on the expanded state.
  const updateState = useCallback(() => {
    if (!parentContainerRef.current || !rect.current) return;
    if (isExpanded) {
      parentContainerRef.current.style.height = `${rect.current.height}px`;
    } else {
      parentContainerRef.current.style.height = '0px';
    }
  }, [isExpanded, parentContainerRef]);

  useEffect(() => {
    updateState();
  }, [updateState, isExpanded, childContainerUpdate]);

  return (
    <div
      className={cn({
        'border-b': !isNoBorder,
        'border-b-0': isNoBorder,
        ...(className ? { [className]: true } : {}),
      })}
    >
      <div
        className="flex justify-between items-center cursor-pointer"
        onClick={toggleExpanded}
        onKeyDown={toggleExpanded}
        role="button"
        tabIndex={0}
      >
        {typeof title === 'string' ? <Paragraph>{title}</Paragraph> : title}

        <div
          className={cn({
            relative: true,
            'transform rotate-180': !isExpanded,
          })}
        >
          <ChevronUpIcon />
        </div>
      </div>
      <div
        className="w-full text-base border-rp-gray-divider overflow-hidden transition-all ease-in-out duration-300"
        ref={parentContainerRef}
      >
        <div ref={childContainerRef}>{children}</div>
      </div>
    </div>
  );
}
